import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { Link, Outlet } from '@tanstack/react-router';
import { useDocumentParams } from 'app/router/params';
import { NavbarDefault } from 'app/components';
import LocaleBadge from 'app/components/layout/LocaleBadge';
import { useUser } from 'app/state/contexts/UserContext';
import { usePermissions } from 'app/api/permission-queries';
import { SupportModal } from 'app/components';
import { useGetDocument, type EditorDocument } from 'entities/document';
import { useSetEditorDocument } from 'app/api/documents/useEditorData';

const Project = () => {
  const { t } = useTranslation(['project-details', 'tag']);

  const { documentId } = useDocumentParams();
  const { selectedOrganization, organizations } = useUser();
  const { documentPermissions } = usePermissions(documentId);
  const companyName = organizations?.find((organization) => selectedOrganization === organization.id)?.name ?? '';
  const setEditorDocument = useSetEditorDocument();
  const { data: document } = useGetDocument(documentId, selectedOrganization) as { data?: EditorDocument };
  useEffect(() => {
    if (document) {
      setEditorDocument(document);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document]);

  if (!document) {
    return <div>loading...</div>;
  }
  return (
    <div className="project--details grid--container full--height columns">
      <div className="content overflow-hidden">
        <div className="project--details-header">
          <div className="content-title">
            <div className="company-header">
              <Link to="/">{companyName}</Link>
            </div>
            <h1 className="d-flex align-items-center gap-2">
              {document.name} <LocaleBadge locale={document.content?.locale} />
            </h1>
          </div>
        </div>
        <div className="menu--layout">
          {/* v5 `<NavLink>` → typed `<Link>`; `exact` → activeOptions, active class → activeProps. */}
          <Link to="/documents/$documentId" params={{ documentId }} activeOptions={{ exact: true }} activeProps={{ className: 'active' }}>
            {t('project-details:nav-link.project-overview')}
          </Link>
          <Link to="/documents/$documentId/metadata" params={{ documentId }} activeProps={{ className: 'active' }}>
            {t('project-details:nav-link.metadata')}
          </Link>
          {documentPermissions.includes('ADMIN') && (
            <Link to="/documents/$documentId/permissions" params={{ documentId }} activeProps={{ className: 'active' }}>
              {t('project-details:nav-link.users')}
            </Link>
          )}
          {(documentPermissions.includes('DESIGN') || documentPermissions.includes('CODER')) && (
            <Link to="/documents/$documentId/pictures" params={{ documentId }} activeProps={{ className: 'active' }}>
              {t('project-details:nav-link.pictures')}
            </Link>
          )}
          {documentPermissions.includes('DB') && (
            <Link to="/documents/$documentId/reference-data" params={{ documentId }} activeProps={{ className: 'active' }}>
              {t('project-details:nav-link.reference-data')}
            </Link>
          )}
          {documentPermissions.includes('ADMIN') && (
            <Link to="/documents/$documentId/language" params={{ documentId }} activeProps={{ className: 'active' }}>
              {t('project-details:nav-link.locale')}
            </Link>
          )}
          {(documentPermissions.includes('DESIGN') || documentPermissions.includes('CODER')) && (
            <Link to="/documents/$documentId/design" params={{ documentId }} activeProps={{ className: 'active' }}>
              {t('project-details:nav-link.layout')}
            </Link>
          )}
          {documentPermissions.includes('TAG_ADMIN') && (
            <Link to="/documents/$documentId/tagging" params={{ documentId }} activeProps={{ className: 'active' }}>
              {t('tag:tagging')}
            </Link>
          )}
        </div>

        <div className="project-details-wrapper">
          {/* Tabs are real routes in app/router/routes.tsx now (the nested <Switch> is gone). */}
          <Outlet />
        </div>
      </div>
      <NavbarDefault />
      <SupportModal />
    </div>
  );
};

export default Project;
